/**
 * Which notes may share a beat in Synth VR (docs/spec/srvr/placement.md SRVR-0337):
 *   - a Special (One Hand or Both Hands) is the ONLY note on its beat;
 *   - a Left or Right note may share its beat with one other note, which must be the opposite hand;
 *   - never more than two notes on one beat.
 *
 * Pure functions over note lists, so the store and the placement click ask the same question and the
 * arithmetic is unit-tested without a store.
 */
import type { Note } from '../types/beatmap';

/** Notes this close in time (ms) are on the same beat: the window the Same-Note-Overwrite option uses. */
export const SAME_BEAT_MS = 2;

export type NoteKind = 'left' | 'right' | 'special';

export function noteKind(n: Pick<Note, 'hand' | 'note_type'>): NoteKind {
  const t = (n.note_type || '').toLowerCase();
  if (n.hand === 'special' || t.includes('special')) return 'special';
  return n.hand === 'left' ? 'left' : 'right';
}

/** Why the notes on one beat are not allowed, or null when they are. */
export function beatProblem(group: ReadonlyArray<Pick<Note, 'hand' | 'note_type'>>): string | null {
  if (group.length <= 1) return null;
  if (group.some(n => noteKind(n) === 'special')) return 'A Special note must be the only note on its beat';
  if (group.length > 2) return "Can't place more than 2 notes on one beat";
  if (noteKind(group[0]) === noteKind(group[1])) return "Can't place 2 notes of the same color";
  return null;
}

/** Cluster time-sorted notes into beats: notes within SAME_BEAT_MS of the cluster's first note. */
function clusters(sorted: ReadonlyArray<Note>): Note[][] {
  const out: Note[][] = [];
  let cur: Note[] = [];
  for (const n of sorted) {
    if (cur.length && n.time_ms - cur[0].time_ms >= SAME_BEAT_MS) { out.push(cur); cur = []; }
    cur.push(n);
  }
  if (cur.length) out.push(cur);
  return out;
}

/**
 * The first reason `next` breaks the rule at a beat where `prev` did not, or null. A beat that was
 * already wrong in `prev` (a map loaded with one) is only a problem if it got worse, so an edit
 * elsewhere in such a map, and a removal on the bad beat itself, are never refused.
 * Both lists must be sorted by time.
 */
export function newBeatProblem(prev: ReadonlyArray<Note>, next: ReadonlyArray<Note>): string | null {
  const before = clusters(prev);
  const sizeBefore = new Map<number, { size: number; problem: boolean }>();
  for (const g of before) sizeBefore.set(Math.round(g[0].time_ms / SAME_BEAT_MS), { size: g.length, problem: beatProblem(g) !== null });
  for (const g of clusters(next)) {
    const problem = beatProblem(g);
    if (!problem) continue;
    // Compare with the nearest beat of `prev`: its key, or the one either side of it.
    const key = Math.round(g[0].time_ms / SAME_BEAT_MS);
    const old = sizeBefore.get(key) ?? sizeBefore.get(key - 1) ?? sizeBefore.get(key + 1);
    if (old && old.problem && g.length <= old.size) continue;   // already wrong, and no worse
    return problem;
  }
  return null;
}

/** Why `candidate` cannot be added to `notes`, or null when it can. */
export function placementProblem(notes: ReadonlyArray<Note>, candidate: Pick<Note, 'time_ms' | 'hand' | 'note_type'>): string | null {
  const group = notes.filter(n => Math.abs(n.time_ms - candidate.time_ms) < SAME_BEAT_MS);
  return beatProblem([...group, candidate]);
}
